import { all, put, select, takeEvery } from 'redux-saga/effects'
import {
  APPLICATION_INITIALISED,
  LOAD_STEP,
  NEXT,
  PREVIOUS,
  USER_SELECT_CHILD,
  USER_SELECT_SLOT,
  USER_SELECT_STORE,
  USER_SELECT_TYPE,
  USER_SUBMIT_FORM,
} from '@types'
import {
  MARKETING_STATE_ACTIVE,
  MARKETING_STATE_FRESH,
  STEP_CONFIRMATION,
  STEP_DATETIME,
  STEP_ERROR,
  STEP_LOCATION,
  STEP_REVIEW,
  STEP_TYPE,
} from '@names'
import {
  loadStep,
  nextState,
  previousState,
  setIcsFile,
  setMarketingState,
  userSelectChild,
  userSelectStore,
  userSelectType,
  userSubmitForm,
} from '@store/actions/app'
import config from '@config'
import { callPromise, postBooking } from '@api'
import { scrollTo, track } from '@helpers'

let isFirstScroll = true

export function* handleApplicationInitialised() {
  const { store, type, step, child } = config.get('local') || {}

  if (type) {
    yield put(userSelectType(type))
  }

  if (store) {
    yield put(userSelectStore(store))
  }

  if (child) {
    yield put(userSelectChild(true))
  }

  yield put(loadStep(step || STEP_TYPE))
}

export function* handleNext() {
  const step = yield select((s) => s.app.step)
  const type = yield select((s) => s.data.type.name)

  switch (step) {
    case STEP_TYPE:
      yield put(loadStep(STEP_LOCATION))
      yield put(nextState(true, true))
      yield put(previousState(true, false))
      track('appointment.progress', { view: step, type })
      break
    case STEP_LOCATION:
      const store = yield select((s) => s.data.store.code)

      yield put(loadStep(STEP_DATETIME))
      yield put(nextState(true, true))
      yield put(previousState(true, false))
      track('appointment.progress', { view: step, type, store })
      break
    case STEP_DATETIME:
      yield put(loadStep(STEP_REVIEW))
      yield put(nextState(true, true))
      yield put(previousState(true, false))
      track('appointment.progress', { view: step, type })
      break
    case STEP_REVIEW:
      yield put(userSubmitForm())
      break
    default:
  }
}

export function* handlePrevious() {
  const step = yield select((s) => s.app.step)

  switch (step) {
    case STEP_LOCATION:
      yield put(loadStep(STEP_TYPE))
      break
    case STEP_DATETIME:
      yield put(loadStep(STEP_LOCATION))
      break
    case STEP_REVIEW:
      yield put(loadStep(STEP_DATETIME))
      break
    default:
  }
}

export function* handleSubmitForm() {
  const templateId = yield select((s) => s.data.type.id)
  const branchId = yield select((s) => s.data.store.amparexId)
  const storeId = yield select((s) => s.data.store.postId)
  const mode = yield select((s) => s.data.mode)
  const slot = yield select((s) => s.data.slot)
  const form = yield select((s) => s.data.form)
  const marketing = yield select((s) => s.data.marketing)
  const locale = config.get('local.locale')
  const userData = yield select((s) => s.data)

  if (marketing === MARKETING_STATE_FRESH) {
    yield put(setMarketingState(MARKETING_STATE_ACTIVE))
    return
  }

  yield put(nextState(true, true, true))

  const trackingPayload = {
    bookingType: userData?.type?.name,
    clientType: userData?.child ? 'pediatric' : 'adult',
    bookingDate: userData?.slot?.date,
    bookingTime: userData?.slot?.startTime,
    fiCode: userData?.store?.code,
    fiName: userData?.store?.contact?.formattedAddress,
  }

  track('appointment.requested', {
    mode: mode,
    track: trackingPayload,
    form: form,
  })

  const payload = {
    formId: config.get('local.formId'),
    statusId: config.get('local.statusId'),
    branchId,
    storeId,
    templateId,
    mode,
    slotTime: slot.startTime,
    slotDate: slot.date,
    form,
    locale,
  }

  const [url, options] = postBooking(payload)

  const response = yield callPromise(url, options)

  yield put(nextState(false, true))
  yield put(previousState(false, true))

  if (response.success) {
    if (response?.ics) {
      yield put(setIcsFile(response.ics))
    }

    yield put(loadStep(STEP_CONFIRMATION))
    // track('appointment.progress', { view: STEP_CONFIRMATION })

    track('appointment.success', {
      mode: mode,
      track: { ...trackingPayload, ...{ id: response?.id } },
    })
  } else {
    yield put(loadStep(STEP_ERROR))
    track('appointment.progress', { view: STEP_ERROR })
    track('appointment.error', {})
  }
}

export function* handleLoadStep({ payload }) {
  switch (payload) {
    case STEP_TYPE:
      yield put(nextState(true, true))
      yield put(previousState(false, false))
      break
    case STEP_LOCATION:
      yield put(nextState(true, true))
      yield put(previousState(true, false))
      break
    case STEP_DATETIME:
      yield put(nextState(true, true))
      yield put(previousState(true, false))
      break
    case STEP_REVIEW:
      yield put(nextState(true, true))
      yield put(previousState(true, false))
      break
    default:
  }

  setTimeout(() => {
    if (isFirstScroll) {
      isFirstScroll = false

      if (config.get('local.features.block_initial_scroll') === true) {
        return false
      }
    }

    scrollTo('appointments-root', { align: { top: 0, topOffset: 50 } })
  }, 1000)
}

export function* handleUserSelectType({ payload }) {
  if (!payload) {
    return
  }
  yield scrollTo()
}

export function* handleUserSelectChild({ payload }) {
  if (!payload) {
    return
  }
  yield scrollTo()
}

export function* handleUserSelectStore({ payload }) {
  if (!payload) {
    return
  }
  yield scrollTo()
}

export function* handleUserSelectSlot({ payload }) {
  if (!payload) {
    return
  }
  yield scrollTo()
}

// eslint-disable-next-line func-names
export default function* () {
  yield all([
    takeEvery(APPLICATION_INITIALISED, handleApplicationInitialised),
    takeEvery(NEXT, handleNext),
    takeEvery(PREVIOUS, handlePrevious),
    takeEvery(USER_SUBMIT_FORM, handleSubmitForm),
    takeEvery(LOAD_STEP, handleLoadStep),
    takeEvery([USER_SELECT_TYPE], handleUserSelectType),
    takeEvery([USER_SELECT_CHILD], handleUserSelectChild),
    takeEvery([USER_SELECT_STORE], handleUserSelectStore),
    takeEvery([USER_SELECT_SLOT], handleUserSelectSlot),
  ])
}
